import axios from 'axios';
import {
  PendingOrdersApiResponse,
  PendingOrderApiItem,
  TransformedPendingOrder,
  ApiOrderSummaryStats
} from '../types/pendingOrdersApi';

// Get token from sessionStorage for authentication
const getAuthToken = () => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  return jsonData?.token;
};

// Transform API data to internal format
const transformApiOrderToInternal = (apiOrder: PendingOrderApiItem): TransformedPendingOrder => {
  // Determine priority based on waiting time and status
  let priority: 'low' | 'normal' | 'high' | 'critical' = 'normal';
  if (apiOrder.status === 'escalated') {
    priority = 'critical';
  } else if (apiOrder.status === 'flagged') {
    priority = 'high';
  } else if (apiOrder.waitingTime >= 30) {
    priority = 'critical';
  } else if (apiOrder.waitingTime >= 15) {
    priority = 'high';
  }

  // Determine if escalated
  const isEscalated = apiOrder.status === 'escalated';

  // Generate estimated delivery time (assuming orders should be delivered within 30-45 minutes)
  const now = new Date();
  const estimatedDeliveryTime = new Date(now.getTime() + (45 - apiOrder.waitingTime) * 60 * 1000);
  
  // Generate order creation time
  const createdAt = new Date(now.getTime() - apiOrder.waitingTime * 60 * 1000);

  return {
    id: apiOrder.orderId,
    orderNumber: `#FB${apiOrder.orderId.slice(-6).toUpperCase()}`,
    customer: {
      id: `cust_${apiOrder.orderId}`,
      name: apiOrder.customerName,
      phone: 'N/A', // Not provided in API
      email: undefined,
      address: 'N/A' // Not provided in API
    },
    restaurant: {
      id: `rest_${apiOrder.restaurantName.toLowerCase().replace(/\s+/g, '_')}`,
      name: apiOrder.restaurantName,
      phone: apiOrder.restaurantPhone,
      email: undefined,
      address: 'N/A' // Not provided in API
    },
    items: [
      // Mock items since not provided in API
      {
        id: `item_${apiOrder.orderId}_1`,
        name: 'Food Item',
        quantity: 1,
        price: apiOrder.totalAmount
      }
    ],
    total: apiOrder.totalAmount,
    status: apiOrder.status === 'normal' ? 'pending' : apiOrder.status,
    priority,
    createdAt: createdAt.toISOString(),
    estimatedDeliveryTime: estimatedDeliveryTime.toISOString(),
    waitingTime: apiOrder.waitingTime,
    isEscalated,
    escalationReason: isEscalated ? 'Order delayed beyond acceptable time' : undefined,
    escalatedTo: isEscalated ? 'Operations Manager' : undefined,
    escalatedAt: isEscalated ? new Date().toISOString() : undefined,
    lastUpdated: new Date().toISOString(),
    paymentMethod: 'cash', // Default since not provided
    paymentStatus: 'pending', // Default since not provided
    deliveryFee: Math.round(apiOrder.totalAmount * 0.1), // Estimate 10% as delivery fee
    serviceFee: Math.round(apiOrder.totalAmount * 0.05), // Estimate 5% as service fee
    tax: Math.round(apiOrder.totalAmount * 0.075), // Estimate 7.5% as tax
    orderType: 'delivery', // Default since not provided
    progress: getProgressByStatus(apiOrder.status, apiOrder.waitingTime)
  };
};

// Helper function to calculate progress based on status and waiting time
const getProgressByStatus = (status: string, waitingTime: number): number => {
  if (status === 'escalated') return 50;
  if (status === 'flagged') return 60;
  
  // For normal status, progress based on waiting time
  if (waitingTime < 5) return 20;
  if (waitingTime < 15) return 40;
  if (waitingTime < 30) return 60;
  return 80;
};

// Calculate summary stats from transformed orders
const calculateSummaryStats = (orders: TransformedPendingOrder[]): ApiOrderSummaryStats => {
  const totalPending = orders.filter(o => 
    ['pending', 'normal', 'flagged', 'escalated'].includes(o.status)
  ).length;
  
  const lateOrders = orders.filter(o => 
    new Date(o.estimatedDeliveryTime) < new Date() && 
    !['delivered', 'cancelled'].includes(o.status)
  ).length;
  
  const escalated = orders.filter(o => o.isEscalated).length;
  
  const averageWaitTime = orders.length > 0 
    ? Math.round(orders.reduce((acc, o) => acc + o.waitingTime, 0) / orders.length)
    : 0;
  
  const totalRevenue = orders.reduce((acc, o) => acc + o.total, 0);
  
  return {
    totalPending,
    lateOrders,
    escalated,
    averageWaitTime,
    totalRevenue,
    completionRate: 85.5 // Default completion rate
  };
};

// API Service Class
class PendingOrdersApiService {
  private baseURL = process.env.REACT_APP_RESTAURANT_URL;

  // Get pending orders from API
  async getPendingOrders(): Promise<{
    success: boolean;
    data: TransformedPendingOrder[];
    stats: ApiOrderSummaryStats;
    message?: string;
    error?: string;
  }> {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }

      console.log('🔄 Fetching pending orders from API...');

      const response = await axios.get<PendingOrdersApiResponse>(
        `${this.baseURL}/pending-orders`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.data.success) {
        throw new Error('Failed to fetch pending orders');
      }

      console.log('✅ Pending orders fetched successfully:', response.data);

      // Transform API data to internal format
      const transformedOrders = response.data.data.map(transformApiOrderToInternal);
      
      // Calculate summary stats
      const stats = calculateSummaryStats(transformedOrders);

      return {
        success: true,
        data: transformedOrders,
        stats,
        message: 'Pending orders fetched successfully'
      };

    } catch (error: any) {
      console.error('❌ Error fetching pending orders:', error);
      
      const errorMessage = error.response?.data?.message 
        || error.message 
        || 'Failed to fetch pending orders';

      return {
        success: false,
        data: [],
        stats: {
          totalPending: 0,
          lateOrders: 0,
          escalated: 0,
          averageWaitTime: 0,
          totalRevenue: 0,
          completionRate: 0
        },
        error: errorMessage
      };
    }
  }

  // Get order by ID (mock implementation since API doesn't provide this endpoint)
  async getOrderById(id: string): Promise<TransformedPendingOrder | null> {
    try {
      // For now, we'll fetch all orders and find the specific one
      const result = await this.getPendingOrders();
      if (result.success) {
        return result.data.find(order => order.id === id) || null;
      }
      return null;
    } catch (error) {
      console.error('Error fetching order by ID:', error);
      return null;
    }
  }

  // Mock escalation functions (since API endpoints are not provided)
  async getEscalationReasons() {
    // Mock data
    return [
      {
        id: 'reason-1',
        name: 'Delay',
        description: 'Order is experiencing delays in preparation or delivery',
        severity: 'high' as const
      },
      {
        id: 'reason-2',
        name: 'Restaurant Not Responding',
        description: 'Restaurant is not responding to calls or messages',
        severity: 'critical' as const
      },
      {
        id: 'reason-3',
        name: 'Customer Complaint',
        description: 'Customer has raised concerns about the order',
        severity: 'medium' as const
      }
    ];
  }

  async getEscalationRecipients() {
    // Mock data
    return [
      {
        id: 'recipient-1',
        name: 'Operations Manager',
        role: 'Operations Manager',
        department: 'Operations',
        email: 'ops@foodbank.com',
        phone: '+234 567 867 9000'
      },
      {
        id: 'recipient-2',
        name: 'Customer Support Lead',
        role: 'Support Lead',
        department: 'Customer Service',
        email: 'support@foodbank.com',
        phone: '+234 567 867 9001'
      }
    ];
  }

  // Real-time updates simulation (since WebSocket is not implemented)
  startRealTimeUpdates(callback: (orders: TransformedPendingOrder[]) => void): () => void {
    const interval = setInterval(async () => {
      try {
        const result = await this.getPendingOrders();
        if (result.success) {
          callback(result.data);
        }
      } catch (error) {
        console.error('Error in real-time updates:', error);
      }
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }
}

export const pendingOrdersApiService = new PendingOrdersApiService();
export default pendingOrdersApiService;